import { useEffect, useCallback, useRef } from "react";
import { isBrowser, isTabbable } from "@thebouqs/utils";
// this hook automatically disables scroll and enables keyboard controls, also tears down when the component is removed
export var useFocusTrap = _ref => {
  var {
    node,
    initial,
    final,
    onClose,
    trap
  } = _ref;
  var firstTabbable = useRef(null);
  var lastTabbable = useRef(null); // get first and last focusable elements

  var setFocusRange = root => {
    var focusable = root.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    firstTabbable.current = focusable[0];
    lastTabbable.current = focusable[focusable.length - 1];
  };

  var keyDownFn = useCallback(ev => {
    if (ev.key === "Esc" || ev.key === "Escape") {
      onClose();
    }
  }, [onClose]);
  useEffect(() => {
    if (!isBrowser || !node.current || !trap) {
      return;
    }

    setFocusRange(node.current); // element to return to onClose

    var aux = document.activeElement; // focus initial element

    document.addEventListener("keydown", keyDownFn);

    if (initial != null && initial.current) {
      initial.current.focus();
    } else {
      var _firstTabbable$curren;

      (_firstTabbable$curren = firstTabbable.current) == null ? void 0 : _firstTabbable$curren.focus();
    } // check if scroll is already disabled by other component


    var bodyStyle = document.body.style;
    var shouldDisable = bodyStyle.overflow !== "hidden"; // disable scroll

    if (shouldDisable) {
      var windowScroll = [window.scrollX, window.scrollY];
      bodyStyle.height = "100%";
      bodyStyle.overflow = "hidden";
      window.scrollTo(windowScroll[0], windowScroll[1]);
    }

    var _final = final == null ? void 0 : final.current;

    return () => {
      // restore focus control
      document.removeEventListener("keydown", keyDownFn);

      if (_final) {
        _final.focus();
      } else {
        aux == null ? void 0 : aux.focus();
      } // restore scroll


      if (shouldDisable) {
        bodyStyle.position = "";
        bodyStyle.height = "";
        bodyStyle.overflow = "";
      }
    };
  }, [initial, final, node, trap, keyDownFn]); // prevents focus from leaving node onBlur

  var focusTrap = useCallback(ev => {
    // adding check ev.currentTarget helps prevent storybook error where ev.currentTarget is undefined
    if (ev.currentTarget && ev.currentTarget.contains(ev.relatedTarget) || !(ev != null && ev.relatedTarget) || !ev.isTrusted || !trap) {
      // not leaving container, do nothing
      // or not navigating with tab
      // note: relatedTarget is null when the element is focusable but not tabbable
      // programatically called
      return;
    }

    if (ev.target.isSameNode(firstTabbable.current)) {
      var _lastTabbable$current;

      // leaving backwards
      (_lastTabbable$current = lastTabbable.current) == null ? void 0 : _lastTabbable$current.focus();
    } else if (ev.target.isSameNode(lastTabbable.current)) {
      var _firstTabbable$curren2;

      // leaving forward
      (_firstTabbable$curren2 = firstTabbable.current) == null ? void 0 : _firstTabbable$curren2.focus();
    } else if (!isTabbable(ev.target)) {
      var _firstTabbable$curren3;

      // leaving via focused, non-tabbable node
      // not a perfect guess but avoid unrecoverable states
      // happens if the user switches between mouse and tab navigation
      (_firstTabbable$curren3 = firstTabbable.current) == null ? void 0 : _firstTabbable$curren3.focus();
    }

    return false;
  }, [firstTabbable, lastTabbable, trap]);
  return focusTrap;
};
//# sourceMappingURL=use-focus-trap.js.map